/**
 * Browser-safe coordinate formatting/parsing utilities for the web portal.
 *
 * The sibling React Native utility lazy-loads these libraries with `require`,
 * which is not available in Vite's browser ESM runtime.
 */

import { forward as mgrsForward, toPoint as mgrsToPoint } from 'mgrs';

const EARTH_RADIUS = 20037508.342789244;

export type CoordinateFormat = 'dd' | 'dms' | 'ddm' | 'utm' | 'mgrs' | 'gars' | 'usng';

export interface CoordinateParseResult {
  success: true;
  lat: number;
  lng: number;
}

export interface CoordinateParseError {
  success: false;
  message: string;
}

export type ParseCoordinateResult = CoordinateParseResult | CoordinateParseError;

export function xyzToTms(z: number, x: number, y: number): { z: number; x: number; y: number } {
  return { z, x, y: (Math.pow(2, z) - 1) - y };
}

export function tmsToXyz(z: number, x: number, y: number): { z: number; x: number; y: number } {
  return xyzToTms(z, x, y);
}

export function epsg3857ToEpsg4326(x: number, y: number): { lng: number; lat: number } {
  const lng = (x / EARTH_RADIUS) * 180;
  const lat = (Math.atan(Math.exp((y / EARTH_RADIUS) * Math.PI)) * 360) / Math.PI - 90;
  return { lng, lat };
}

export function epsg4326ToEpsg3857(lng: number, lat: number): { x: number; y: number } {
  const x = (lng * EARTH_RADIUS) / 180;
  const y = (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / Math.PI) * EARTH_RADIUS;
  return { x, y };
}

export function formatCoordinateDisplay(lat: number, lng: number, format?: CoordinateFormat): string {
  switch (format) {
    case 'dms':
      return formatDMS(lat, lng);
    case 'ddm':
      return formatDDM(lat, lng);
    case 'utm':
      return formatUTM(lat, lng);
    case 'mgrs':
      return formatMGRS(lat, lng);
    case 'gars':
      return formatGARS(lat, lng);
    case 'usng':
      return formatUSNG(lat, lng);
    case 'dd':
    default: {
      const latDir = lat >= 0 ? 'N' : 'S';
      const lngDir = lng >= 0 ? 'E' : 'W';
      return `${latDir} ${Math.abs(lat).toFixed(6)}°, ${lngDir} ${Math.abs(lng).toFixed(6)}°`;
    }
  }
}

function formatDMS(lat: number, lng: number): string {
  const toDMS = (v: number) => {
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const mFull = (abs - d) * 60;
    const m = Math.floor(mFull);
    const s = (mFull - m) * 60;
    return `${d}°${String(m).padStart(2, '0')}'${s.toFixed(1).padStart(4, '0')}"`;
  };
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${latDir} ${toDMS(lat)}, ${lngDir} ${toDMS(lng)}`;
}

function formatDDM(lat: number, lng: number): string {
  const toDDM = (v: number) => {
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const m = (abs - d) * 60;
    return `${d}°${m.toFixed(4)}'`;
  };
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${latDir} ${toDDM(lat)}, ${lngDir} ${toDDM(lng)}`;
}

function formatUTM(lat: number, lng: number): string {
  const clampedLat = Math.max(-80, Math.min(84, lat));
  const zone = Math.floor((lng + 180) / 6) + 1;
  const letter = getUTMLatBand(clampedLat);

  const latRad = (clampedLat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const centralMeridian = (((zone - 1) * 6 - 180 + 3) * Math.PI) / 180;

  const a = 6378137;
  const f = 1 / 298.257223563;
  const e2 = 2 * f - f * f;
  const ep2 = e2 / (1 - e2);
  const n = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
  const t = Math.tan(latRad) * Math.tan(latRad);
  const c = ep2 * Math.cos(latRad) * Math.cos(latRad);
  const aCoeff = Math.cos(latRad) * (lngRad - centralMeridian);

  const m = a * (
    (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * latRad
    - (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * latRad)
    + (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * latRad)
    - (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * latRad)
  );

  const easting = 500000 + 0.9996 * n * (
    aCoeff
    + ((1 - t + c) * aCoeff ** 3) / 6
    + ((5 - 18 * t + t * t + 72 * c - 58 * ep2) * aCoeff ** 5) / 120
  );

  let northing = 0.9996 * (m + n * Math.tan(latRad) * (
    (aCoeff ** 2) / 2
    + ((5 - t + 9 * c + 4 * c * c) * aCoeff ** 4) / 24
    + ((61 - 58 * t + t * t + 600 * c - 330 * ep2) * aCoeff ** 6) / 720
  ));

  if (clampedLat < 0) northing += 10000000;
  return `${zone}${letter} ${Math.round(easting)}mE ${Math.round(northing)}mN`;
}

function getUTMLatBand(lat: number): string {
  const bands = 'CDEFGHJKLMNPQRSTUVWX';
  const idx = Math.floor((lat + 80) / 8);
  return bands[Math.min(idx, bands.length - 1)] ?? 'X';
}

function formatMGRS(lat: number, lng: number): string {
  const clampedLat = Math.max(-80, Math.min(84, lat));
  return mgrsForward([lng, clampedLat], 5);
}

function formatGARS(lat: number, lng: number): string {
  const clampedLat = Math.max(-90, Math.min(90, lat));
  let clampedLng = lng;
  if (clampedLng >= 180) clampedLng = 179.999999;
  if (clampedLng < -180) clampedLng = -180;
  const lonBand = Math.floor((clampedLng + 180) * 2) + 1;
  const latBand = Math.min(359, Math.max(0, Math.floor((clampedLat + 90) * 2)));
  const letters = garsBandLetters(latBand);
  const lonRemainder = ((clampedLng + 180) * 2) % 1;
  const latRemainder = ((clampedLat + 90) * 2) % 1;
  const quadrant = garsQuadrant(lonRemainder, latRemainder);
  const keypad = garsKeypad(lonRemainder, latRemainder);
  return `${String(lonBand).padStart(3, '0')}${letters}${quadrant}${keypad}`;
}

function formatUSNG(lat: number, lng: number): string {
  const clampedLat = Math.max(-80, Math.min(84, lat));
  const compact = mgrsForward([lng, clampedLat], 5);
  const match = compact.match(/^(\d{1,2}[A-Z])([A-Z]{2})(\d{10})$/);
  if (!match) return compact;
  return `${match[1]} ${match[2]} ${match[3].slice(0, 5)} ${match[3].slice(5)}`;
}

const GARS_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ';

function garsBandLetters(latBand: number): string {
  const first = Math.floor(latBand / GARS_LETTERS.length);
  const second = latBand % GARS_LETTERS.length;
  return `${GARS_LETTERS[first]}${GARS_LETTERS[second]}`;
}

function garsLetterIndex(first: string, second: string): number {
  const firstIndex = GARS_LETTERS.indexOf(first);
  const secondIndex = GARS_LETTERS.indexOf(second);
  if (firstIndex < 0 || secondIndex < 0) throw new Error('invalid GARS latitude band');
  return firstIndex * GARS_LETTERS.length + secondIndex;
}

function garsQuadrant(lonRemainder: number, latRemainder: number): number {
  if (latRemainder >= 0.5) return lonRemainder < 0.5 ? 1 : 2;
  return lonRemainder < 0.5 ? 3 : 4;
}

function garsKeypad(lonRemainder: number, latRemainder: number): number {
  const col = Math.min(2, Math.floor((lonRemainder % 0.5) * 6));
  const rowFromSouth = Math.min(2, Math.floor((latRemainder % 0.5) * 6));
  return (2 - rowFromSouth) * 3 + col + 1;
}

export function parseCoordinateString(input: string, format: CoordinateFormat): ParseCoordinateResult {
  const trimmed = input.trim();
  if (!trimmed) return { success: false, message: 'Coordinate string is empty' };

  switch (format) {
    case 'mgrs':
      return parseMGRS(trimmed);
    case 'gars':
      return parseGARS(trimmed);
    case 'usng':
      return parseUSNG(trimmed);
    default:
      return { success: false, message: `Format '${format}' is not supported for parsing` };
  }
}

function parseMGRS(input: string): ParseCoordinateResult {
  try {
    const [lng, lat] = mgrsToPoint(input);
    return { success: true, lat, lng };
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    return { success: false, message: `Invalid MGRS coordinate: ${detail}` };
  }
}

function parseGARS(input: string): ParseCoordinateResult {
  try {
    const match = input.toUpperCase().match(/^(\d{3})([A-HJ-NP-Z]{2})([1-4])?([1-9])?$/);
    if (!match) throw new Error('expected NNNLL, optionally with quadrant and keypad');

    const lonBand = Number(match[1]);
    if (lonBand < 1 || lonBand > 720) throw new Error('longitude band out of range');

    const latBand = garsLetterIndex(match[2][0], match[2][1]);
    if (latBand > 359) throw new Error('latitude band out of range');

    let lonOffset = 0.25;
    let latOffset = 0.25;
    const quadrant = match[3] ? Number(match[3]) : undefined;
    if (quadrant) {
      const west = quadrant === 1 || quadrant === 3;
      const north = quadrant === 1 || quadrant === 2;
      lonOffset = west ? 0.125 : 0.375;
      latOffset = north ? 0.375 : 0.125;
    }

    const keypad = match[4] ? Number(match[4]) : undefined;
    if (quadrant && keypad) {
      const keyIndex = keypad - 1;
      const col = keyIndex % 3;
      const rowFromNorth = Math.floor(keyIndex / 3);
      const quadrantLonBase = quadrant === 1 || quadrant === 3 ? 0 : 0.25;
      const quadrantLatBase = quadrant === 1 || quadrant === 2 ? 0.25 : 0;
      lonOffset = quadrantLonBase + (col + 0.5) / 12;
      latOffset = quadrantLatBase + ((2 - rowFromNorth) + 0.5) / 12;
    }

    return {
      success: true,
      lng: -180 + (lonBand - 1) * 0.5 + lonOffset,
      lat: -90 + latBand * 0.5 + latOffset,
    };
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    return { success: false, message: `Invalid GARS coordinate: ${detail}` };
  }
}

function parseUSNG(input: string): ParseCoordinateResult {
  try {
    const compact = input.replace(/\s+/g, '');
    const [lng, lat] = mgrsToPoint(compact);
    return { success: true, lat, lng };
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    return { success: false, message: `Invalid USNG coordinate: ${detail}` };
  }
}
